import React, { useState, useEffect, useCallback } from 'react';
import { Car, Search, Eye, Trash2, CheckCircle, XCircle, X } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import EmptyState from '../common/EmptyState';
import { AdminVehicleAPI, AdminCompanyAPI, APIUtils } from '../../services/api';

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    AdminCompanyAPI.getAll({ limit: 200, status: 'approved' }).then(res => {
      if (res.data.success) setCompanies(res.data.data.companies);
    }).catch(() => {});
  }, []);

  const fetchVehicles = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.approvalStatus = statusFilter;
      if (companyFilter) params.companyId = companyFilter;
      if (search) params.search = search;

      const response = await AdminVehicleAPI.getAll(params);
      if (response.data.success) {
        setVehicles(response.data.data.vehicles);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Fetch vehicles error:', APIUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, companyFilter, search]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const handleApproval = async (vehicleId, approvalStatus) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.updateApproval(vehicleId, approvalStatus, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivate = async (vehicleId) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.deactivate(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (vehicleId) => {
    setActionLoading(true);
    try {
      await AdminVehicleAPI.reactivate(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to remove this vehicle? This cannot be undone.')) return;
    setActionLoading(true);
    try {
      await AdminVehicleAPI.delete(vehicleId, actionNotes);
      setSelectedVehicle(null);
      setActionNotes('');
      fetchVehicles();
    } catch (error) {
      alert(APIUtils.handleError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const statusTabs = ['all', 'pending', 'approved', 'rejected'];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
        <p className="text-gray-500 mt-1">Manage vehicle registrations and approvals</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex space-x-1">
            {statusTabs.map((tab) => (
              <button key={tab} onClick={() => setStatusFilter(tab)} className={`px-3 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors ${statusFilter === tab ? 'bg-emerald-100 text-emerald-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                {tab}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>{c.companyName}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input type="text" placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
        ) : vehicles.length === 0 ? (
          <EmptyState title="No vehicles found" message="No vehicles match the current filters." icon={Car} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License Plate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{vehicle.year} {vehicle.vehicleMake} {vehicle.model}</p>
                        <p className="text-sm text-gray-500">{vehicle.color} - {vehicle.capacity} seats</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{vehicle.company?.companyName || '-'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-700">{vehicle.licensePlate}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{vehicle.vehicleType}</td>
                      <td className="px-6 py-4"><StatusBadge status={vehicle.approvalStatus || 'pending'} /></td>
                      <td className="px-6 py-4"><StatusBadge status={vehicle.status || 'active'} /></td>
                      <td className="px-6 py-4">
                        {/* Mobile-friendly action buttons: 44px+ tap targets
                            with visible labels; wrap on narrow screens. */}
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => setSelectedVehicle(vehicle)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                            title="View details"
                          >
                            <Eye size={18} />
                            <span>View</span>
                          </button>
                          {vehicle.approvalStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Approve ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) {
                                    handleApproval(vehicle._id, 'approved');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                                title="Approve"
                              >
                                <CheckCircle size={18} />
                                <span>Approve</span>
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Reject ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) {
                                    handleApproval(vehicle._id, 'rejected');
                                  }
                                }}
                                disabled={actionLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50"
                                title="Reject"
                              >
                                <XCircle size={18} />
                                <span>Reject</span>
                              </button>
                            </>
                          )}
                          {vehicle.approvalStatus === 'approved' && vehicle.status === 'active' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Deactivate ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) {
                                  handleDeactivate(vehicle._id);
                                }
                              }}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-2 min-h-[44px] text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 disabled:opacity-50"
                            >
                              Deactivate
                            </button>
                          )}
                          {vehicle.approvalStatus === 'approved' && vehicle.status === 'inactive' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Reactivate ${vehicle.vehicleMake} ${vehicle.model} (${vehicle.licensePlate})?`)) {
                                  handleReactivate(vehicle._id);
                                }
                              }}
                              disabled={actionLoading}
                              className="inline-flex items-center px-3 py-2 min-h-[44px] text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50"
                            >
                              Reactivate
                            </button>
                          )}
                          {(vehicle.status === 'inactive' || vehicle.approvalStatus === 'rejected') && (
                            <button
                              onClick={() => handleDelete(vehicle._id)}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                              title="Remove"
                            >
                              <Trash2 size={16} />
                              <span>Remove</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
                <div className="flex space-x-2">
                  <button onClick={() => fetchVehicles(pagination.page - 1)} disabled={pagination.page <= 1} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
                  <button onClick={() => fetchVehicles(pagination.page + 1)} disabled={pagination.page >= pagination.pages} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{selectedVehicle.year} {selectedVehicle.vehicleMake} {selectedVehicle.model}</h3>
              <button onClick={() => { setSelectedVehicle(null); setActionNotes(''); }} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">License Plate</p><p className="font-medium font-mono">{selectedVehicle.licensePlate}</p></div>
                <div><p className="text-sm text-gray-500">Company</p><p className="font-medium">{selectedVehicle.company?.companyName}</p></div>
                <div><p className="text-sm text-gray-500">Type</p><p className="font-medium capitalize">{selectedVehicle.vehicleType}</p></div>
                <div><p className="text-sm text-gray-500">Color</p><p className="font-medium">{selectedVehicle.color}</p></div>
                <div><p className="text-sm text-gray-500">Capacity</p><p className="font-medium">{selectedVehicle.capacity} passengers</p></div>
                <div><p className="text-sm text-gray-500">Fuel</p><p className="font-medium capitalize">{selectedVehicle.fuelType}</p></div>
                <div><p className="text-sm text-gray-500">Transmission</p><p className="font-medium capitalize">{selectedVehicle.transmission}</p></div>
                <div><p className="text-sm text-gray-500">Approval</p><StatusBadge status={selectedVehicle.approvalStatus || 'pending'} /></div>
                <div><p className="text-sm text-gray-500">Status</p><StatusBadge status={selectedVehicle.status || 'active'} /></div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none" rows={2} placeholder="Add notes..." />
                <div className="flex space-x-3 mt-3">
                  {selectedVehicle.approvalStatus === 'pending' && (
                    <>
                      <button onClick={() => handleApproval(selectedVehicle._id, 'approved')} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Approve</button>
                      <button onClick={() => handleApproval(selectedVehicle._id, 'rejected')} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Reject</button>
                    </>
                  )}
                  {selectedVehicle.approvalStatus === 'approved' && selectedVehicle.status === 'active' && (
                    <button onClick={() => handleDeactivate(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 text-sm">Deactivate</button>
                  )}
                  {selectedVehicle.approvalStatus === 'approved' && selectedVehicle.status === 'inactive' && (
                    <button onClick={() => handleReactivate(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 text-sm">Reactivate</button>
                  )}
                  {(selectedVehicle.status === 'inactive' || selectedVehicle.approvalStatus === 'rejected') && (
                    <button onClick={() => handleDelete(selectedVehicle._id)} disabled={actionLoading} className="flex-1 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium disabled:opacity-50 text-sm">Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleList;
